import { useState, useMemo, useRef } from 'react';
import React from 'react';
import {
    Paper, Button, Tooltip, Chip,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import {
    Add as AddIcon,
    Edit as EditIcon,
    FileCopy as FileCopyIcon,
    DeleteForever as DeleteForeverIcon,
    Settings as SettingsIcon,
    ArrowRightRounded as ArrowRight,
    ArrowDropDownRounded as ArrowDown,
    Visibility as ViewOnlyIcon,
} from '@material-ui/icons';
import { Checkbox, Table } from 'rsuite';
import useAvailableHeight from '../../hooks/useAvailableHeight';
import OnClickInterpreter from '../OnClickInterpreter';
import { dynamicSort } from '../../services/sort';
import statusColorMap from '../StatusMultiSelect/statusColorMap.json';
import './TableContent.scss';
import { columnWidthMap } from '../DataviewTable';
import { DataviewValue } from '..';
import { AdminDetailsTooltip } from '../../../../EmploymentBillboard/Admin/components/ConfigurationSetTable/AdminDetailsTooltip';


const { Cell, Column, HeaderCell } = Table;

/** @type {CommonClient2.SortType} */
let defaultSortTypeIfUnset;
// eslint-disable-next-line prefer-const
defaultSortTypeIfUnset = 'asc';

/**
 * @template T
 * @typedef {{id: string|number} & T} TableContentData
 */

/**
 * @param {{
 *  data: TableContentData[];
 *  columns: CommonClient2.TableStaticColumn[];
 *  statusFilterField?: string;
 *  nameColumnKey?: string;
 *  nameColumnChipKey?: string;
 *  defaultSortColumnKey: string;
 *  defaultSortType?: CommonClient2.SortType;
 *  isRowsSelectable?: boolean;
 *  tableId?: string;
 *  onAddRound?: (event: React.MouseEvent<HTMLElement>, value: T) => void;
 *  onChange?: (event: React.ChangeEvent, value: T) => void,
 *  onEdit?: (event: React.MouseEvent<HTMLElement>, value: T) => void;
 *  onDuplicate?: (event: React.MouseEvent<HTMLElement>, value: T) => void;
 *  onDelete?: (event: React.MouseEvent<HTMLElement>, value: T) => void;
 *  onManage?: (event: React.MouseEvent<HTMLElement>, value: T) => void;
 *  onNameCellClick?: (event: React.MouseEvent<HTMLElement>, value: T) => void;
 *  fillAvailableHeight?: boolean;
 *  selectedItems: string[];
 *  treeChildrenKey?: string; // to be use with isTree
 * } & import('rsuite-table').TableProps} param0
 */
const DashboardContent = ({
    data,
    columns,
    statusFilterField,
    nameColumnKey = 'name',
    nameColumnChipKey = '',
    defaultSortType = defaultSortTypeIfUnset,
    defaultSortColumnKey,
    isRowsSelectable = false,
    onAddRound,
    onChange,
    onEdit,
    onDuplicate,
    onDelete,
    onColumnResize,
    onManage,
    onNameCellClick,
    fillAvailableHeight = false,
    height,
    selectedItems,
    treeChildrenKey,
    ...others
}) => {
    const ref = useRef(null);
    const [sortType, setSortType] = useState(defaultSortType);
    const [sortColumn, setSortColumn] = useState(defaultSortColumnKey || '');
    const [checkedKeys, setCheckedKeys] = useState(selectedItems || []);


    /** @type {number[]} */
    const initExpandedRows = [];
    const [expandedRows, setExpandedRows] = useState(initExpandedRows);

    const sortKey = sortType === 'desc' ? `-${sortColumn}` : sortColumn;
    const sortedData = useMemo(() => data?.sort(dynamicSort(sortKey)), [JSON.stringify(data), sortType, sortColumn]);
    const { t } = useTranslation(['table']);

    /**
     * @param {string} newSortColumn
     * @param {CommonClient2.SortType=} newSortType
     */
    const handleSortColumn = (newSortColumn, newSortType) => {
        setSortColumn(newSortColumn);
        // @ts-ignore
        setSortType(newSortType);
    };

    const onSelectionChange = (keys) => {
        const stringifiedKeys = keys.map((k) => k.toString());
        setCheckedKeys(stringifiedKeys);
        if (onChange) {
            onChange(stringifiedKeys);
        }
    };

    const handleCheckAll = (_, checked) => {
        // @ts-ignore
        const keys = checked ? data.map((item) => item.id) : [];
        onSelectionChange(keys);
    };

    const handleCheck = (value, checked) => {
        const keys = checked ? [...checkedKeys, value] : checkedKeys.filter((item) => item !== value.toString());
        onSelectionChange(keys);
    };

    const hasActions = !!(onEdit || onDuplicate || onDelete);
    const nameCellClickAction = onManage || onEdit;
    let nameCellClick = onNameCellClick;
    if (!onNameCellClick && nameCellClickAction) {
        nameCellClick = (value, event) => nameCellClickAction(event, value);
    }

    const checked = checkedKeys.length === data?.length;
    const indeterminate = !!checkedKeys.length && checkedKeys.length < data?.length;

    const autoHeightValue = useAvailableHeight(ref, fillAvailableHeight);

    const getData = () => {
        if (others.isTree && treeChildrenKey) {
            return sortedData.map((d) => (
                { ...d, children: d[treeChildrenKey] || [] }
            ));
        }

        return sortedData;
    };

    return (
        <Paper
            elevation={2}
            className="core--table-content"
            ref={ref}
        >
            <Table
                data={getData()}
                sortType={sortType}
                sortColumn={sortColumn}
                onSortColumn={handleSortColumn}
                rowClassName="core--table-row"
                shouldUpdateScroll={false}
                height={height || autoHeightValue}
                renderTreeToggle={(icon, rowData) => {
                    if (!rowData?.children || rowData.children.length === 0) {
                        return <></>;
                    }
                    const isRowExpanded = expandedRows.includes(rowData.id);
                    return (isRowExpanded) ? <ArrowDown /> : <ArrowRight />;
                }}
                onExpandChange={(expanded, rowData) => {
                    let newExpandedRows = [];
                    if (expanded) {
                        expandedRows.push(rowData.id);
                        newExpandedRows = expandedRows;
                    } else {
                        newExpandedRows = expandedRows.filter((id) => id !== rowData.id);
                    }
                    setExpandedRows(newExpandedRows);
                }}
                {...others}
            >
                {
                    isRowsSelectable && (
                        <Column
                            width={50}
                            align="center"
                        >
                            <HeaderCell className="header-checkbox-cell">
                                <Checkbox
                                    inline
                                    checked={checked}
                                    indeterminate={indeterminate}
                                    onChange={handleCheckAll}
                                />
                            </HeaderCell>
                            <Cell
                                rowData={null}
                                style={{ padding: 0 }}
                            >
                                {(rowData) => (
                                    <div style={{ lineHeight: '46px' }}>
                                        <Checkbox
                                            value={rowData.id}
                                            inline
                                            onChange={handleCheck}
                                            checked={checkedKeys.some((item) => item.toString() === rowData.id.toString())}
                                        />
                                    </div>
                                )}
                            </Cell>
                        </Column>
                    )
                }
                {(columns || []).map(({
                    key, tKey, dataValueType, isTooltip, ...rest
                }) => {
                    if (key === statusFilterField) {
                        return null;
                    }

                    if (key === nameColumnKey) {
                        return (
                            <Column
                                {...rest}
                                key={key}
                                onResize={onColumnResize ? (newWidth) => onColumnResize(key, newWidth) : undefined}
                            >
                                <HeaderCell>{t(tKey)}</HeaderCell>
                                <Cell
                                    className="core--status-name-cell"
                                    dataKey={key}
                                >
                                    {(rowData) => {
                                        const cellContentClasses = ['core--status-name-cell-content'];
                                        if (others?.isTree) {
                                            if (!rowData.children) {
                                                cellContentClasses.push('cell-content--children-row-margin');
                                            } else if (rowData.children.length === 0) {
                                                cellContentClasses.push('cell-content--no-chldren-row-margin');
                                            }
                                        }
                                        return (
                                            <AdminDetailsTooltip data={rowData} translate={"AdminRuleSet."}>
                                            <div
                                                className={cellContentClasses.join(' ')}
                                                // @ts-ignore
                                                onClick={() => nameCellClick(rowData)}
                                            >
                                                {statusFilterField
                                                    && (
                                                        <Tooltip
                                                            title={rowData[statusFilterField]}
                                                            classes={{ tooltip: 'core--status-tooltip' }}
                                                        >
                                                            <div
                                                                className="core--status-cell"
                                                                style={{ backgroundColor: statusColorMap[rowData[statusFilterField]] }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                <div
                                                    className="core--tooltip-cell"
                                                >
                                                        <span>
                                                            {rowData[key]}
                                                        </span>
                                                    {
                                                        !!nameColumnChipKey
                                                        && rowData[nameColumnChipKey]
                                                        && <Chip
                                                            className="name-columm-chip"
                                                            label={rowData[nameColumnChipKey]}
                                                        />
                                                    }
                                                </div>
                                            </div>
                                            </AdminDetailsTooltip>
                                        );
                                    }}
                                </Cell>
                            </Column>
                        );
                    }

                    if (isTooltip) {
                        return (
                            <Column
                                {...rest}
                                key={key}
                            >
                                <HeaderCell>{t(tKey)}</HeaderCell>
                                <Cell dataKey={key}>
                                    {(rowData) => (
                                        <div
                                            className="core--tooltip-cell"
                                        >
                                            <Tooltip title={rowData[key]}>
                                                <span>{rowData[key]}</span>
                                            </Tooltip>
                                        </div>
                                    )}
                                </Cell>
                            </Column>
                        );
                    }

                    if (dataValueType) {
                        return (
                            <Column
                                {...rest}
                                key={key}
                                minWidth={columnWidthMap[dataValueType]}
                            >
                                <HeaderCell>{t(tKey)}</HeaderCell>
                                <Cell dataKey={key}>
                                    {(rowData) => (
                                        <DataviewValue
                                            dataValueType={dataValueType}
                                            value={rowData[key]}
                                        />
                                    )}
                                </Cell>
                            </Column>
                        );
                    }

                    return (
                        <Column
                            {...rest}
                            key={key}
                            onResize={onColumnResize ? (newWidth) => onColumnResize(key, newWidth) : undefined}
                        >
                            <HeaderCell>{t(tKey)}</HeaderCell>
                            <Cell dataKey={key} />
                        </Column>
                    );
                })}
                {hasActions && (
                    <Column
                        minWidth={250}
                        flexGrow={2}
                        fixed="right"
                    >
                        <HeaderCell> </HeaderCell>
                        <Cell>
                            {(rowData) => {
                                const {
                                    isAddRoundDisabled,
                                    isAddRoundDisplayed,
                                    isEditDisabled,
                                    isEditDisplayed = true,
                                    isDuplicateDisabled,
                                    isDeleteDisabled,
                                    isManageDisabled,
                                } = rowData?.actionButtons || {};

                                /**
                                 * TODO - When the button in the tooltip is disabled, this error is thrown in the console:
                                 * "index.js:1 Material-UI: You are providing a disabled `button` child to the Tooltip component.
                                 *  A disabled element does not fire events.
                                 *  Tooltip needs to listen to the child element's events to display the title."
                                 *
                                 *  Solution: Add a wrapper (div, span, fragment etc). The events needs to be refatored then
                                 */

                                return (
                                    <div
                                        className="core--action-button-container"
                                    >
                                        {
                                            (!!onAddRound && isAddRoundDisplayed) && (
                                                <OnClickInterpreter>
                                                    <Tooltip title={t('actions.addRound') || ''}>
                                                        <Button
                                                            onClick={(event) => {
                                                                // @ts-ignore
                                                                onAddRound(event, rowData);
                                                            }}
                                                            className={
                                                                `core--tablerow-action-button add-round ${isAddRoundDisabled && 'disabled'}`
                                                            }
                                                            disabled={isAddRoundDisabled}
                                                        >
                                                            <AddIcon fontSize="inherit" />
                                                            <span>{t('actions.addRound')}</span>
                                                        </Button>
                                                    </Tooltip>
                                                </OnClickInterpreter>
                                            )
                                        }
                                        {
                                            (!!onEdit && isEditDisplayed) && (
                                                <OnClickInterpreter>
                                                    <Tooltip title={(isEditDisabled ? t('actions.viewOnly') : t('actions.edit')) || ''}>
                                                        <Button
                                                            onClick={(event) => {
                                                                // @ts-ignore
                                                                onEdit(event, rowData);
                                                            }}
                                                            className="core--tablerow-action-button"
                                                        >
                                                            {isEditDisabled
                                                                ? <ViewOnlyIcon fontSize="inherit" />
                                                                : <EditIcon fontSize="inherit" />}

                                                        </Button>
                                                    </Tooltip>
                                                </OnClickInterpreter>
                                            )
                                        }
                                        {
                                            !!onDuplicate && (
                                                <OnClickInterpreter>
                                                    <Tooltip title={t('actions.duplicate') || ''}>
                                                        <Button
                                                            onClick={(event) => {
                                                                // @ts-ignore
                                                                onDuplicate(event, rowData);
                                                            }}
                                                            className={`core--tablerow-action-button ${isDuplicateDisabled && 'disabled'}`}
                                                            disabled={isDuplicateDisabled}
                                                        >
                                                            <FileCopyIcon fontSize="inherit" />
                                                        </Button>
                                                    </Tooltip>
                                                </OnClickInterpreter>
                                            )
                                        }
                                        {
                                            !!onDelete && (
                                                <OnClickInterpreter>
                                                    <Tooltip title={t('actions.delete') || ''}>
                                                        <Button
                                                            onClick={(event) => {
                                                                // @ts-ignore
                                                                onDelete(event, rowData);
                                                            }}
                                                            className={`core--tablerow-action-button ${isDeleteDisabled && 'disabled'}`}
                                                            disabled={isDeleteDisabled}
                                                        >
                                                            <DeleteForeverIcon fontSize="inherit" />
                                                        </Button>
                                                    </Tooltip>
                                                </OnClickInterpreter>
                                            )
                                        }
                                        {
                                            !!onManage && (
                                                <OnClickInterpreter>
                                                    <Button
                                                        onClick={(event) => {
                                                            // @ts-ignore
                                                            onManage(event, rowData);
                                                        }}
                                                        className={`core--tablerow-action-button ${isManageDisabled && 'disabled'}`}
                                                        disabled={isManageDisabled}
                                                    >
                                                        <SettingsIcon fontSize="inherit" />
                                                        <span>{t('actions.manage')}</span>
                                                    </Button>
                                                </OnClickInterpreter>
                                            )
                                        }
                                    </div>
                                );
                            }}
                        </Cell>
                    </Column>
                )}
            </Table>
        </Paper>
    );
};
export default DashboardContent;
